// ?modal=agency-cash on Setup's Agency tab (finance redesign slice 4, J18): the agency's cash on a day already gone, which
// /finance's Cash counts from, plus everything paid in and out after that day. A save replaces the one figure. Below zero
// is a figure (an overdrawn account), not an error. No Cancel: the X closes (J12, J26).
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { ModalPending } from "../components/ModalPending";
import { MissingLine, Required } from "../components/Required";
import { useAgencyCashFigure, useSaveAgencyCash } from "../hooks/useAgencyFinance";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { AgencyCashRead } from "../types";
import { dateLabel } from "../utils/format";
import { closeUnlessPending } from "../utils/mutations";
import { AGENCY_CASH_HINT, agencyCashCheck } from "./cashText";

const LABEL = "Agency cash";
const FORM_ID = "agency-cash-form";

/** Change once a figure exists, as the card's button says, and Set while none does. */
const title = (read: AgencyCashRead) => `${read.agencyCash ? "Change" : "Set"} · ${LABEL}`;

export function AgencyCashDrawer({ onClose }: { onClose: () => void }) {
  const query = useAgencyCashFigure();
  if (!query.data) {
    return (
      <ModalPending
        title={LABEL}
        onClose={onClose}
        failure={query.isError ? "Could not load the agency's cash." : null}
        retrying={query.isFetching}
        retry={() => void query.refetch()}
      />
    );
  }
  return <AgencyCashForm read={query.data} onClose={onClose} />;
}

function AgencyCashForm({ read, onClose }: { read: AgencyCashRead; onClose: () => void }) {
  const save = useSaveAgencyCash();
  const [asOf, setAsOf] = useState(read.agencyCash?.asOf ?? read.today);
  const [amount, setAmount] = useState<number | null>(read.agencyCash?.amountMinor ?? null);
  const check = agencyCashCheck(asOf, amount, read.today);
  const send = useSubmitOnce();
  const submit = () => {
    if (check.blocked || amount === null) return;
    send(() => save.mutateAsync({ asOf, amountMinor: amount }), () => onClose());
  };
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(save.isPending, onClose)}
      title={title(read)}
      footer={
        <Button type="submit" form={FORM_ID} disabled={check.blocked || save.isPending} isLoading={save.isPending}>
          Save
        </Button>
      }
    >
      <form
        id={FORM_ID}
        className="space-y-3 text-sm"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-gray-600">{`${ENTITY_CONFIG.finance.label}'s Cash counts from this figure, plus everything paid in and out after its day.`}</p>
        <FormField label={<Required>On</Required>} error={check.dateError ?? undefined}>
          <input
            type="date"
            className={inputClassName}
            aria-label="On"
            aria-required="true"
            max={read.today}
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </FormField>
        <FormField label={<Required>{`Agency cash on ${dateLabel(asOf || null)}`}</Required>} hint={AGENCY_CASH_HINT}>
          <MoneyInput valueMinor={amount} currency="EUR" onChange={setAmount} ariaLabel="Agency cash" required autoFocus />
        </FormField>
        <MissingLine button="Save" missing={amount === null ? ["the amount"] : []} />
      </form>
    </FinanceDrawer>
  );
}
